import React from 'react';
import styled from 'styled-components';
import {
  formatPlacementDate,
  placementContextLabel,
  placementCustomerName,
  placementEmployeeName,
  placementHasEnded,
  placementIsCurrent,
  placementIsPlanned,
} from '../../utils/adminV2';

const Wrap = styled.div`
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const Guidance = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Scroll = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 620px;
`;

const Cell = styled.td`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Head = styled.th`
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  text-align: left;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.backgroundLight};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  white-space: nowrap;
`;

const Status = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ $current, theme }) => ($current ? theme.colors.success : theme.colors.textSecondary)};
  background: ${({ $current, theme }) => ($current ? `${theme.colors.success}15` : theme.colors.backgroundLight)};
`;

const Empty = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const PlacementHistory = ({
  placements = [],
  customers = [],
  employees = [],
  perspective = 'employee',
  emptyMessage = 'No placement history is available.',
  showGuidance = true,
}) => {
  const sorted = [...placements].sort((a, b) => (
    String(b.startDate || '').localeCompare(String(a.startDate || ''))
    || Number(b.id || 0) - Number(a.id || 0)
  ));

  if (sorted.length === 0) return <Empty>{emptyMessage}</Empty>;

  return (
    <Wrap>
      {showGuidance && (
        <Guidance>
          Reassignment ends the current placement and starts a new one. Previous placements remain in history and are not overwritten.
        </Guidance>
      )}
      <Scroll>
        <Table>
          <thead>
            <tr>
              <Head>{perspective === 'customer' ? 'Employee' : 'Customer'}</Head>
              <Head>Period</Head>
              <Head>Status</Head>
              <Head>Source</Head>
            </tr>
          </thead>
          <tbody>
            {sorted.map((placement, index) => {
              // A cancelled booking retains its original dates. Date-based
              // labels must never make that retained history look live later.
              const cancelled = Boolean(placement.cancelledAt || placement.supersededByPlacementId
                || ['cancelled', 'canceled'].includes(String(placement.status || '').toLowerCase()));
              const current = !cancelled && placementIsCurrent(placement);
              const planned = !cancelled && !current && placementIsPlanned(placement);
              return (
                <tr key={placement.id || `${placement.customerId}-${placement.employeeId}-${placement.startDate}-${index}`}>
                  <Cell>
                    {perspective === 'customer'
                      ? placementEmployeeName(placement, employees)
                      : placementCustomerName(placement, customers)}
                  </Cell>
                  <Cell>{formatPlacementDate(placement.startDate)} – {formatPlacementDate(placement.endDate)}</Cell>
                  <Cell>
                    <Status $current={current}>
                      {cancelled ? 'Cancelled' : current ? 'Current'
                        : planned ? 'Planned'
                          : placementHasEnded(placement) ? 'Ended' : (placement.status || 'Ended')}
                    </Status>
                  </Cell>
                  <Cell>{placementContextLabel(placement)}</Cell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Scroll>
    </Wrap>
  );
};

export default PlacementHistory;
